import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import '../styles/components/SummaryTable.css';
import { downloadCsv, buildCsvFromColumns } from '../utils/csvUtils';
import { exportTableAsPdf } from '../utils/pdfExport';

type SortDirection = 'asc' | 'desc';

export interface SummaryItem {
  id: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  tooltip?: string;
  className?: string;
}

export interface SummaryTableColumn<T> {
  key: string;
  label: string | React.ReactNode;
  subtext?: string | React.ReactNode;
  width?: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  render: (row: T) => React.ReactNode;
  sortAccessor?: (row: T) => string | number;
}

export interface SummaryTableRow<T> {
  id: string;
  data: T;
  expandedContent?: React.ReactNode;
}

export interface SummaryTablePdfVariant {
  label: string;
  filenameSuffix?: string;
  expandAllRows?: boolean;
  additionalContent?: (pdfContainer: HTMLElement) => void;
}

export interface SummaryTableExportConfig<T> {
  enabled: boolean;
  filenameBase?: string;
  buildCsvRows?: (rows: SummaryTableRow<T>[]) => string[][];
  pdf?: {
    filterTags?: string[];
    pageInfoText?: string;
    additionalContent?: (pdfContainer: HTMLElement) => void;
    gridTemplateOverride?: string;
    variants?: SummaryTablePdfVariant[];
  };
}

export interface SummaryTableProps<T> {
  summaryItems?: SummaryItem[];
  columns: SummaryTableColumn<T>[];
  rows: SummaryTableRow<T>[];
  defaultSort?: { key: string; direction: SortDirection };
  exportConfig?: SummaryTableExportConfig<T>;
  className?: string;
  searchPlaceholderEntity?: string;
  enableColumnCustomization?: boolean;
}

const getDefaultSortAccessor = (key: string) => (row: any) => {
  const value = row && key in row ? (row as any)[key] : '';
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return value.toLowerCase();
  return String(value ?? '');
};

const getTextValue = (rendered: React.ReactNode, rowData: any, columnKey: string) => {
  if (typeof rendered === 'string' || typeof rendered === 'number') {
    return String(rendered);
  }
  if (rowData && columnKey in rowData) {
    const raw = (rowData as any)[columnKey];
    return raw !== undefined && raw !== null ? String(raw) : '';
  }
  return '';
};

function SummaryTable<T>({
  summaryItems = [],
  columns,
  rows,
  defaultSort,
  exportConfig,
  className,
  searchPlaceholderEntity,
  enableColumnCustomization = false
}: SummaryTableProps<T>) {
  const [sortKey, setSortKey] = useState<string>(defaultSort?.key || columns[0]?.key || '');
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSort?.direction || 'asc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [searchExpanded, setSearchExpanded] = useState(false);
  // Store initial column order for reset functionality
  const initialColumnKeys = useMemo(() => columns.map(col => col.key), [columns]);
  
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() => 
    enableColumnCustomization ? columns.map(col => col.key) : []
  );
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const columnMenuRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    if (!enableColumnCustomization) return columns;
    return visibleColumnKeys
      .map(key => columns.find(col => col.key === key))
      .filter((col): col is SummaryTableColumn<T> => col !== undefined);
  }, [columns, visibleColumnKeys, enableColumnCustomization]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const column = visibleColumns.find(col => col.key === sortKey);
    const accessor = column?.sortAccessor
      ? (row: SummaryTableRow<T>) => column.sortAccessor!(row.data)
      : (row: SummaryTableRow<T>) => getDefaultSortAccessor(sortKey)(row.data);

    const sorted = [...rows].sort((a, b) => {
      const aVal = accessor(a);
      const bVal = accessor(b);

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return aVal - bVal;
      }
      const aStr = String(aVal ?? '').toLowerCase();
      const bStr = String(bVal ?? '').toLowerCase();
      if (aStr < bStr) return -1;
      if (aStr > bStr) return 1;
      return 0;
    });

    return sortDirection === 'asc' ? sorted : sorted.reverse();
  }, [rows, visibleColumns, sortKey, sortDirection]);

  const expandableRowIds = useMemo(
    () => sortedRows.filter(row => row.expandedContent).map(row => row.id),
    [sortedRows]
  );

  const hasExpandableRows = expandableRowIds.length > 0;

  const gridTemplateColumns = useMemo(() => {
    const columnWidths = visibleColumns.map(col => col.width || '1fr');
    if (hasExpandableRows) {
      columnWidths.push('56px'); // Fixed width for expand/collapse controls
    }
    return columnWidths.join(' ');
  }, [visibleColumns, hasExpandableRows]);

  const onSort = (key: string, sortable?: boolean) => {
    if (!sortable) return;
    if (sortKey === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const toggleRow = (rowId: string, expandable: boolean) => {
    if (!expandable) return;
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleExportCsv = useCallback(() => {
    if (!exportConfig?.enabled) return;

    const filenameBase = exportConfig.filenameBase || 'summary-table';
    if (exportConfig.buildCsvRows) {
      const rowsForCsv = exportConfig.buildCsvRows(sortedRows);
      downloadCsv(rowsForCsv, filenameBase);
      return;
    }

    // Helper to convert label to string
    const labelToString = (label: string | React.ReactNode): string => {
      if (typeof label === 'string') return label;
      if (typeof label === 'number') return String(label);
      // For ReactNode, try to extract text or use column key as fallback
      return '';
    };

    const columnsForCsv = visibleColumns.map(col => ({
      header: labelToString(col.label) || col.key,
      value: (item: SummaryTableRow<T>) => {
        const rendered = col.render(item.data);
        return getTextValue(rendered, item.data, col.key);
      }
    }));

    const rowsForCsv = buildCsvFromColumns(sortedRows, columnsForCsv);
    downloadCsv(rowsForCsv, filenameBase);
  }, [exportConfig, sortedRows, visibleColumns]);

  const handleExportPdf = useCallback((variant?: SummaryTablePdfVariant) => {
    if (!exportConfig?.enabled || !tableContainerRef.current) return;

    const filenameBase = exportConfig.filenameBase || 'summary-table';
    const ts = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const suffix = variant?.filenameSuffix ? `-${variant.filenameSuffix}` : '';
    const filename = `${filenameBase}${suffix}-${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}.pdf`;

    // For PDF, exclude the expand/collapse column from grid template
    const pdfGridTemplate = exportConfig.pdf?.gridTemplateOverride || 
      (hasExpandableRows 
        ? visibleColumns.map(col => col.width || '1fr').join(' ') // Exclude the 56px expand/collapse column
        : gridTemplateColumns);
    
    const baseAdditional = exportConfig.pdf?.additionalContent;
    const variantAdditional = variant?.additionalContent;

    const combinedAdditional = (container: HTMLElement) => {
      if (baseAdditional) baseAdditional(container);
      if (variantAdditional) variantAdditional(container);
    };

    exportTableAsPdf({
      container: tableContainerRef.current,
      filename,
      gridTemplateColumns: pdfGridTemplate,
      headerSelector: '.summary-table-header',
      rowSelector: '.summary-table-row',
      wrapperSelector: '.summary-table-row-wrapper',
      contentContainerSelector: '.summary-table-container',
      removeSelectors: [],
      filterTags: exportConfig.pdf?.filterTags || [],
      pageInfoText: exportConfig.pdf?.pageInfoText,
      additionalContent: combinedAdditional,
      additionalContainerClasses: ['summary-table-pdf'],
      prepareClone: (clone) => {
        // Hide expand/collapse controls in PDF
        clone.querySelectorAll('.summary-table-header-expand-controls').forEach(el => {
          (el as HTMLElement).style.display = 'none';
        });
        clone.querySelectorAll('.summary-table-row-expand-cell').forEach(el => {
          (el as HTMLElement).style.display = 'none';
        });
        
        // Handle row expansion/collapse based on variant
        if (variant?.expandAllRows) {
          // Expand all rows
          clone.querySelectorAll('.summary-table-row').forEach(el => el.classList.add('expanded'));
          clone.querySelectorAll('.summary-table-row-details').forEach(el => {
            el.classList.add('expanded');
            (el as HTMLElement).style.display = 'block';
          });
        } else {
          // Collapse all rows
          clone.querySelectorAll('.summary-table-row').forEach(el => el.classList.remove('expanded'));
          clone.querySelectorAll('.summary-table-row-details').forEach(el => {
            el.classList.remove('expanded');
            (el as HTMLElement).style.display = 'none';
          });
        }
        
        // Ensure header maintains grid layout
        const header = clone.querySelector('.summary-table-header') as HTMLElement;
        if (header) {
          header.style.display = 'grid';
          header.style.gridTemplateColumns = pdfGridTemplate;
        }
        
        // Apply PDF grid template to rows as well
        clone.querySelectorAll('.summary-table-row').forEach(row => {
          (row as HTMLElement).style.gridTemplateColumns = pdfGridTemplate;
        });
      }
    });
  }, [exportConfig, gridTemplateColumns, visibleColumns, hasExpandableRows]);

  const renderSortIndicator = (column: SummaryTableColumn<T>) => {
    if (!column.sortable) return null;
    const isActive = sortKey === column.key;
    return (
      <div className="summary-table-sort-indicator">
        <span className={`sort-indicator sort-indicator-up ${isActive && sortDirection === 'asc' ? 'sort-indicator-active' : ''}`}>↑</span>
        <span className={`sort-indicator sort-indicator-down ${isActive && sortDirection === 'desc' ? 'sort-indicator-active' : ''}`}>↓</span>
      </div>
    );
  };

  // Column customization handlers
  const toggleColumnVisibility = (columnKey: string) => {
    setVisibleColumnKeys(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(key => key !== columnKey);
      } else {
        return [...prev, columnKey];
      }
    });
  };

  const moveColumn = (columnKey: string, direction: 'up' | 'down') => {
    setVisibleColumnKeys(prev => {
      const index = prev.indexOf(columnKey);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      
      const newKeys = [...prev];
      [newKeys[index], newKeys[newIndex]] = [newKeys[newIndex], newKeys[index]];
      return newKeys;
    });
  };

  const resetColumns = () => {
    setVisibleColumnKeys([...initialColumnKeys]);
  };

  useEffect(() => {
    if (enableColumnCustomization && visibleColumnKeys.length === 0) {
      setVisibleColumnKeys(columns.map(col => col.key));
    }
  }, [enableColumnCustomization, columns]);

  useEffect(() => {
    if (!columnMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setColumnMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [columnMenuOpen]);

  const pdfVariants = exportConfig?.pdf?.variants && exportConfig.pdf.variants.length > 0
    ? exportConfig.pdf.variants
    : exportConfig?.pdf
      ? [{ label: 'PDF' }]
      : [];

  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportMenuOpen]);

  useEffect(() => {
    if (!searchExpanded) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node) &&
        !searchQuery
      ) {
        setSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchExpanded, searchQuery]);

  useEffect(() => {
    if (searchExpanded) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [searchExpanded]);

  useEffect(() => {
    if (searchQuery && !searchExpanded) {
      setSearchExpanded(true);
    }
  }, [searchQuery, searchExpanded]);

  const exportOptions = useMemo(() => {
    const options: Array<{ key: string; label: string; icon: string; action: () => void }> = [];
    options.push({
      key: 'csv',
      label: 'CSV',
      icon: 'table_view',
      action: () => handleExportCsv()
    });
    pdfVariants.forEach((variant, idx) => {
      options.push({
        key: `pdf-${idx}`,
        label: variant.label,
        icon: 'picture_as_pdf',
        action: () => handleExportPdf(variant)
      });
    });
    return options;
  }, [pdfVariants, handleExportCsv, handleExportPdf]);

  const handleExportSelect = useCallback((action: () => void) => {
    action();
    setExportMenuOpen(false);
  }, []);

  rowRefs.current = {};

  const searchPlaceholderLabel = searchPlaceholderEntity?.trim() || 'table';

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const matchingRowIds = useMemo(() => {
    if (!normalizedQuery) return [] as string[];
    const matches: string[] = [];
    sortedRows.forEach(row => {
      const rowText = visibleColumns
        .map(col => {
          const rendered = col.render(row.data);
          return getTextValue(rendered, row.data, col.key).toLowerCase();
        })
        .join(' ');
      if (rowText.includes(normalizedQuery)) {
        matches.push(row.id);
      }
    });
    return matches;
  }, [normalizedQuery, sortedRows, visibleColumns]);

  useEffect(() => {
    if (!normalizedQuery) {
      setCurrentMatchIndex(0);
    }
  }, [normalizedQuery]);

  const matchCount = matchingRowIds.length;
  const safeCurrentIndex = matchCount ? Math.min(currentMatchIndex, matchCount - 1) : 0;
  const activeMatchId = matchCount ? matchingRowIds[safeCurrentIndex] : null;
  const matchingRowSet = useMemo(() => new Set(matchingRowIds), [matchingRowIds]);

  useEffect(() => {
    if (!matchCount) {
      setCurrentMatchIndex(0);
      return;
    }
    if (currentMatchIndex >= matchCount) {
      setCurrentMatchIndex(0);
    }
  }, [matchCount, currentMatchIndex]);

  useEffect(() => {
    if (!matchCount) return;
    const targetId = matchingRowIds[safeCurrentIndex];
    const targetRef = rowRefs.current[targetId];
    if (!targetRef) return;
    const frame = requestAnimationFrame(() => {
      targetRef.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [matchCount, safeCurrentIndex, matchingRowIds, searchQuery]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentMatchIndex(0);
  };

  const navigateMatch = (direction: number) => {
    if (navDisabled) return;
    setCurrentMatchIndex(prev => {
      const next = (prev + direction + matchCount) % matchCount;
      return next;
    });
  };

  const navDisabled = !matchCount || !normalizedQuery;

  const openSearch = useCallback(() => {
    if (!searchExpanded) {
      setSearchExpanded(true);
    }
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [searchExpanded]);

  const expandAllRows = useCallback(() => {
    setExpandedRows(new Set(expandableRowIds));
  }, [expandableRowIds]);

  const collapseAllRows = useCallback(() => {
    setExpandedRows(new Set());
  }, []);


  return (
    <div className={`summary-table-widget ${className || ''}`}>
      <div className="summary-table-header-bar">
        {summaryItems.length > 0 && (
          <div className="summary-table-summary-items">
            {summaryItems.map(item => (
              <div key={item.id} className={`summary-table-summary-item ${item.className || ''}`} title={item.tooltip}>
                {item.icon && <span className="summary-table-summary-icon">{item.icon}</span>}
                <span className="summary-table-summary-value">{item.value}</span>
              </div>
            ))}
          </div>
        )}
        <div className="summary-table-tools">
          {enableColumnCustomization && (
            <div className="summary-table-column-customization" ref={columnMenuRef}>
              <button
                type="button"
                className="summary-table-column-button"
                onClick={() => setColumnMenuOpen(prev => !prev)}
                title="Customize columns"
                aria-label="Customize columns"
              >
                <span className="material-symbols-outlined">view_column</span>
              </button>
              {columnMenuOpen && (
                <div className="summary-table-column-menu">
                  <div className="summary-table-column-menu-header">
                    <span>Customize Columns</span>
                    <button
                      type="button"
                      className="summary-table-column-reset"
                      onClick={resetColumns}
                      title="Reset to default"
                      aria-label="Reset columns to default"
                    >
                      <span className="material-symbols-outlined">refresh</span>
                      <span>Reset</span>
                    </button>
                  </div>
                  <div className="summary-table-column-menu-list">
                    {(() => {
                      // First, show visible columns in their current order (matching table order)
                      const visibleColumnsInOrder = visibleColumnKeys
                        .map(key => columns.find(col => col.key === key))
                        .filter((col): col is SummaryTableColumn<T> => col !== undefined);
                      
                      // Then, show hidden columns
                      const hiddenColumns = columns.filter(col => !visibleColumnKeys.includes(col.key));
                      
                      // Combine: visible columns first (in order), then hidden columns
                      const orderedColumns = [...visibleColumnsInOrder, ...hiddenColumns];
                      
                      return orderedColumns.map((column, index) => {
                        const isVisible = visibleColumnKeys.includes(column.key);
                        const visibleIndex = visibleColumnKeys.indexOf(column.key);
                        const canMoveUp = isVisible && visibleIndex > 0;
                        const canMoveDown = isVisible && visibleIndex < visibleColumnKeys.length - 1;
                        const labelText = typeof column.label === 'string' ? column.label : column.key;
                        
                        return (
                          <div key={column.key} className="summary-table-column-menu-item">
                            <label className="summary-table-column-checkbox">
                              <input
                                type="checkbox"
                                checked={isVisible}
                                onChange={() => toggleColumnVisibility(column.key)}
                              />
                              <div className="summary-table-column-label-wrapper">
                                <span>{labelText}</span>
                                {column.subtext && (
                                  <span className="summary-table-column-subtext">{column.subtext}</span>
                                )}
                              </div>
                            </label>
                            {isVisible && (
                              <div className="summary-table-column-actions">
                                <button
                                  type="button"
                                  className="summary-table-column-move"
                                  onClick={() => moveColumn(column.key, 'up')}
                                  disabled={!canMoveUp}
                                  title="Move up"
                                  aria-label="Move column up"
                                >
                                  <span className="material-symbols-outlined">arrow_upward</span>
                                </button>
                                <button
                                  type="button"
                                  className="summary-table-column-move"
                                  onClick={() => moveColumn(column.key, 'down')}
                                  disabled={!canMoveDown}
                                  title="Move down"
                                  aria-label="Move column down"
                                >
                                  <span className="material-symbols-outlined">arrow_downward</span>
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
          <div
            className={`summary-table-search ${searchExpanded ? 'expanded' : 'collapsed'}`}
            ref={searchContainerRef}
            title="Search within table"
          >
            {!searchExpanded ? (
              <button
                type="button"
                className="summary-table-search-toggle"
                onClick={openSearch}
                aria-label="Search"
              >
                <span className="material-symbols-outlined">search</span>
              </button>
            ) : (
              <>
                <span className="material-symbols-outlined search-icon">search</span>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={event => handleSearchChange(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      navigateMatch(1);
                    } else if (event.key === 'Escape') {
                      handleSearchChange('');
                    }
                  }}
                  placeholder={`Search ${searchPlaceholderLabel}`}
                />
                <span className={`summary-table-search-divider ${searchQuery ? '' : 'hidden'}`} />
                <div className={`summary-table-search-nav-group ${searchQuery ? '' : 'hidden'}`}>
                  <span className="summary-table-search-count">
                    {matchCount ? `${safeCurrentIndex + 1}/${matchCount}` : '0/0'}
                  </span>
                  <button
                    type="button"
                    className="summary-table-search-nav"
                    onClick={() => navigateMatch(-1)}
                    disabled={navDisabled}
                    aria-label="Previous match"
                  >
                    <span className="material-symbols-outlined">expand_less</span>
                  </button>
                  <button
                    type="button"
                    className="summary-table-search-nav"
                    onClick={() => navigateMatch(1)}
                    disabled={navDisabled}
                    aria-label="Next match"
                  >
                    <span className="material-symbols-outlined">expand_more</span>
                  </button>
                  <button
                    type="button"
                    className="summary-table-search-clear"
                    onClick={() => handleSearchChange('')}
                    aria-label="Clear search"
                    disabled={!searchQuery}
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>
              </>
            )}
          </div>
          {exportConfig?.enabled && (
            <div className="summary-table-export" ref={exportMenuRef}>
              <div className={`export-menu ${exportMenuOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className="export-button"
                  onClick={() => setExportMenuOpen(prev => !prev)}
                  title="Export"
                  aria-haspopup="menu"
                  aria-expanded={exportMenuOpen}
                >
                  <span className="material-symbols-outlined">download</span>
                  {/* <span className="export-button-label">Export</span>
                  <span className="material-symbols-outlined caret">expand_more</span> */}
                </button>
                {exportMenuOpen && (
                  <div className="export-dropdown compact">
                    {exportOptions.map(option => (
                      <button
                        key={option.key}
                        type="button"
                        className="export-dropdown-item"
                        onClick={() => handleExportSelect(option.action)}
                      >
                        <span className="material-symbols-outlined">{option.icon}</span>
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="summary-table-container" ref={tableContainerRef}>
        <div className="summary-table-header" style={{ gridTemplateColumns }}>
          {visibleColumns.map(column => (
            <div
              key={column.key}
              className={`summary-table-header-cell ${column.sortable ? 'sortable' : ''}`}
              onClick={() => onSort(column.key, column.sortable)}
            >
              {renderSortIndicator(column)}
              <div className="header-content">
                <span className="summary-table-header-label">{column.label}</span>
                {column.subtext && <div className="summary-table-header-subtext">{column.subtext}</div>}
              </div>
            </div>
          ))}
          {hasExpandableRows && (
            <div className="summary-table-header-cell summary-table-header-expand-controls">
              <button
                type="button"
                className="summary-table-header-expand-button"
                onClick={expandAllRows}
                title="Expand all rows"
                aria-label="Expand all rows"
              >
                <span className="material-symbols-outlined">keyboard_double_arrow_down</span>
              </button>
              <button
                type="button"
                className="summary-table-header-expand-button"
                onClick={collapseAllRows}
                title="Collapse all rows"
                aria-label="Collapse all rows"
              >
                <span className="material-symbols-outlined">keyboard_double_arrow_up</span>
              </button>
            </div>
          )}
        </div>

        {sortedRows.length === 0 ? (
          <div className="summary-table-empty">No records found</div>
        ) : (
          <div className="summary-table-body">
            {sortedRows.map(row => {
              const isExpanded = expandedRows.has(row.id);
              const isExpandable = Boolean(row.expandedContent);
              const isMatch = matchingRowSet.has(row.id);
              const isActiveMatch = activeMatchId === row.id;
              const rowClassName = `summary-table-row${isExpandable ? ' expandable' : ''}${isExpanded ? ' expanded' : ''}${isMatch ? ' match' : ''}${isActiveMatch ? ' match-active' : ''}`;
              return (
                <div
                  key={row.id}
                  className="summary-table-row-wrapper"
                  ref={el => {
                    rowRefs.current[row.id] = el;
                  }}
                >
                  <div
                    className={rowClassName}
                    style={{ gridTemplateColumns }}
                    onClick={() => toggleRow(row.id, isExpandable)}
                  >
                    {visibleColumns.map(column => {
                      const content = column.render(row.data);
                      const alignClass = column.align ? `align-${column.align}` : '';
                      return (
                        <div key={column.key} className={`summary-table-cell ${alignClass}`}>
                          {content}
                        </div>
                      );
                    })}
                    {hasExpandableRows && (
                      <div className="summary-table-cell summary-table-row-expand-cell"></div>
                    )}
                  </div>
                  {row.expandedContent && (
                    <div className={`summary-table-row-details ${isExpanded ? 'expanded' : ''}`}>
                      {row.expandedContent}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default SummaryTable;


